import { z } from 'zod';
import { ExtensionRuleIdSchema } from './extension.js';
// =============================================================================
// FULFILLMENT CONSTRAINT RULE SCHEMA
// =============================================================================
//
// A rule targets EITHER explicit products OR collections — never both. Modelled as
// a discriminated union on `target` so each variant only carries the ids it needs
// (no "at least one of productIds/collectionIds" refinement, no empty sibling array).
//
// At runtime, a matched product blocks the checkout (before payment) when the shop's
// configured warehouse location(s) cannot cover the requested quantity — i.e. the
// units would have to be sourced from a physical store. The warehouse location(s)
// are NOT part of the rule: they live on `shop.settings.fulfillment.locations.warehouse`.
const ZBaseFulfillmentConstraintRule = z.object({
    id: ExtensionRuleIdSchema,
    enabled: z.boolean(),
});
/**
 * A rule targeting explicit products by id.
 *
 * @example
 * const rule: ExtensionProductFulfillmentConstraintRule = {
 *   id: generateExtensionRuleId(),
 *   enabled: true,
 *   target: 'product',
 *   productIds: ['gid://shopify/Product/7938302247001'],
 * };
 */
export const ZProductFulfillmentConstraintRuleSchema = ZBaseFulfillmentConstraintRule.extend({
    target: z.literal('product'),
    productIds: z.string().array().min(1, 'A product rule must target at least one product'),
}).strict();
/**
 * A rule targeting collections by id (any product belonging to one of them is matched).
 *
 * @example
 * const rule: ExtensionCollectionFulfillmentConstraintRule = {
 *   id: generateExtensionRuleId(),
 *   enabled: true,
 *   target: 'collection',
 *   collectionIds: ['gid://shopify/Collection/288766591065'],
 * };
 */
export const ZCollectionFulfillmentConstraintRuleSchema = ZBaseFulfillmentConstraintRule.extend({
    target: z.literal('collection'),
    collectionIds: z.string().array().min(1, 'A collection rule must target at least one collection'),
}).strict();
/**
 * A single fulfillment-constraint rule: a product rule OR a collection rule.
 */
export const ZFulfillmentConstraintRuleSchema = z.discriminatedUnion('target', [
    ZProductFulfillmentConstraintRuleSchema,
    ZCollectionFulfillmentConstraintRuleSchema,
]);
//# sourceMappingURL=fulfillment-constraints.js.map